import { useState, useEffect } from "react";
import { Loader2, Calendar as CalIcon, Clock, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";

export default function BookingCalendar({ leadId, name, onBooked }) {
  const [days, setDays] = useState([]);
  const [loadingDays, setLoadingDays] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    api
      .get("/availability/days")
      .then(({ data }) => setDays(data.days))
      .catch(() => toast.error("No se pudieron cargar las fechas."))
      .finally(() => setLoadingDays(false));
  }, []);

  const pickDate = async (d) => {
    setSelectedDate(d);
    setSelectedTime(null);
    setLoadingSlots(true);
    try {
      const { data } = await api.get("/availability/slots", { params: { date: d.date } });
      setSlots(data.slots);
    } catch {
      toast.error("No se pudieron cargar las horas.");
    } finally {
      setLoadingSlots(false);
    }
  };

  const confirm = async () => {
    if (!selectedDate || !selectedTime) return;
    setBooking(true);
    try {
      const { data } = await api.post("/bookings", {
        lead_id: leadId,
        date: selectedDate.date,
        time: selectedTime,
      });
      onBooked(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo reservar.");
      if (err.response?.status === 409 && selectedDate) pickDate(selectedDate);
    } finally {
      setBooking(false);
    }
  };

  return (
    <div data-testid="booking-calendar">
      <div className="flex items-center gap-3 mb-8 text-[#34D399]">
        <CheckCircle2 size={20} />
        <span className="text-sm">
          ¡Genial{name ? `, ${name.split(" ")[0]}` : ""}! Ahora elige el día y la hora de tu llamada.
        </span>
      </div>

      {/* Step 1: Days */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4 text-white/50 text-[11px] uppercase tracking-[0.2em] font-display">
          <CalIcon size={14} className="text-[#D4AF37]" /> Selecciona un día
        </div>
        {loadingDays ? (
          <div className="flex items-center gap-2 text-white/40 text-sm py-6">
            <Loader2 className="animate-spin" size={16} /> Cargando fechas...
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-72 overflow-y-auto pr-1">
            {days.map((d) => (
              <button
                key={d.date}
                data-testid={`day-${d.date}`}
                onClick={() => pickDate(d)}
                className={`px-3 py-3 rounded-sm border text-xs md:text-sm text-left transition-all ${
                  selectedDate?.date === d.date
                    ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                    : "border-white/15 text-white/70 hover:border-white/30"
                }`}
              >
                <span className="font-display uppercase tracking-wide block leading-tight">{d.label}</span>
              </button>
            ))}
            {days.length === 0 && (
              <p className="col-span-full text-white/40 text-sm">No hay fechas disponibles por ahora.</p>
            )}
          </div>
        )}
      </div>

      {/* Step 2: Times */}
      {selectedDate && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4 text-white/50 text-[11px] uppercase tracking-[0.2em] font-display">
            <Clock size={14} className="text-[#D4AF37]" /> Selecciona una hora
          </div>
          {loadingSlots ? (
            <div className="flex items-center gap-2 text-white/40 text-sm py-4">
              <Loader2 className="animate-spin" size={16} /> Cargando horas...
            </div>
          ) : slots.length === 0 ? (
            <p className="text-white/40 text-sm">No quedan horas libres este día. Elige otro.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
              {slots.map((t) => (
                <button
                  key={t}
                  data-testid={`slot-${t}`}
                  onClick={() => setSelectedTime(t)}
                  className={`py-3 rounded-sm border text-sm font-display transition-all ${
                    selectedTime === t
                      ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                      : "border-white/15 text-white/70 hover:border-white/30"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {selectedTime && (
        <button
          data-testid="confirm-booking"
          onClick={confirm}
          disabled={booking}
          className="btn-gold rounded-sm w-full py-4 text-sm md:text-base flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {booking ? <Loader2 className="animate-spin" size={18} /> : "Confirmar reserva"}
        </button>
      )}
    </div>
  );
}
